import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { authFetch } from "../utils/authFetch";
import toastr from "toastr";
import { Helmet } from "react-helmet";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/* ---------- Toastr Setup ---------- */
const toast = (m, t = "success") => {
  toastr.options = { closeButton: true, progressBar: true, timeOut: 3000 };
  toastr[t](m);
};

const exportToExcel = (data, fileName = "PayinReports.xlsx") => {
  if (!data || data.length === 0) return;

  const parsedData = data.map((r) => {
    const refString = (r.reference_details || "")
      .replace(/\n,\s*/g, "\n")
      .replace(/,\s*/g, "\n");
    let ref = {};
    refString.split("\n").forEach((line) => {
      const [key, value] = line.split(":").map((s) => s.trim());
      if (key && value !== undefined) ref[key] = value;
    });

    const chargeString = (r.charge || "").replace(/\n,\s*/g, "\n");
    let chargeData = {};
    chargeString.split("\n").forEach((line) => {
      const [key, value] = line.split(":").map((s) => s.trim());
      if (key && value !== undefined) chargeData[key] = value;
    });

    const cleanUser = (r.user_details || "")
      .replace(/\n/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    // 🧮 Convert "₹100.0" → 100.0 (number)
    const parseAmount = (val) => {
      if (!val) return 0;
      const num = parseFloat(val.replace(/[₹,\s-]/g, ""));
      return isNaN(num) ? 0 : num;
    };

    return {
      Date: r.date || "-",
      "User Details": cleanUser || "-",
      "Transaction ID": r.transaction_id || "-",
      "Txn Type": ref["Txn Type"] || "-",
      Mode: ref["Mode"] || r.mode || "-",
      UTR: ref["UTR"] || "-",
      Ref: ref["Ref"] || "-",
      "API TxnId": ref["API TxnId"] || "-",
      Amount: parseAmount(r.amount),
      Charge: parseAmount(chargeData["Charge"]),
      GST: parseAmount(chargeData["GST"]),
      Status: r.status || "-",
      
    };
  });

  const worksheet = XLSX.utils.json_to_sheet(parsedData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Reports");

  // 🔹 Auto column width
  worksheet["!cols"] = Object.keys(parsedData[0]).map((key) => ({
    wch: Math.max(key.length + 2, 18),
  }));

  XLSX.writeFile(workbook, fileName);
};



const exportToPDF = (data, fileName = "PayinReports.pdf") => {
  const doc = new jsPDF();
  doc.text("Payin Reports", 14, 10);

  if (data.length > 0) {
    const tableColumn = ["#", "Date", "User", "Amount", "TxnID", "Status"];
    const tableRows = data.map((row, i) => [
      i + 1,
      row.date || "-",
      row.user_details || "-",
      row.amount || "-",
      row.transaction_id || "-",
      row.status || "-",
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
      theme: "striped",
      headStyles: { fillColor: [22, 160, 133] },
      styles: { fontSize: 10 },
    });
  }

  doc.save(fileName);
};

export default function ReportList() {
  const today = dayjs().format("YYYY-MM-DD");

  const [reports, setReports] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [searchText, setSearchText] = useState("");
  const [filteredReports, setFilteredReports] = useState([]);

  const parseAmount = (val) => {
    if (!val) return 0;
    // Remove ₹, spaces, - sign
    const num = parseFloat(val.replace(/[₹,\s-]/g, ""));
    return isNaN(num) ? 0 : num;
  };
  const parseChargeGST = (chargeStr) => {
    let charge = 0;
    let gst = 0;
    if (chargeStr) {
      // Remove spaces after minus
      const normalized = chargeStr.replace(/₹-\s*/g, "₹-");
      
      // Match numbers with optional negative sign
      const cMatch = normalized.match(/Charge:\s*₹(-?[\d.]+)/);
      const gMatch = normalized.match(/GST:\s*₹(-?[\d.]+)/);

      charge = cMatch ? parseFloat(cMatch[1]) : 0;
      gst = gMatch ? parseFloat(gMatch[1]) : 0;
    }
    return { charge, gst };
  };
  
  const totalAmount = filteredReports.reduce(
    (sum, r) => sum + parseAmount(r.amount),
    0
  );

  const totalCharge = filteredReports.reduce(
    (sum, r) => sum + parseChargeGST(r.charge).charge,
    0
  );

  const totalGST = filteredReports.reduce(
    (sum, r) => sum + parseChargeGST(r.charge).gst,
    0
  );

  

  const totalTxn = filteredReports.length;

  const successTxn = filteredReports.filter(
    (r) => (r.status || "").toLowerCase() === "success"
  ).length;

  const successRatio =
    totalTxn > 0 ? ((successTxn / totalTxn) * 100).toFixed(2) : "0.00";

  const loadReports = async () => {
    let url = `/api/v1/report/list?from_date=${fromDate}&to_date=${toDate}`;
    if (statusFilter) url += `&status=${statusFilter}`;
    if (selectedUser) url += `&user_id=${selectedUser}`;
    if (searchText) url += `&search=${encodeURIComponent(searchText)}`;

    try {
      const r = await authFetch(url);
      if (!r.ok) throw new Error(`HTTP error! status: ${r.status}`);
      const d = await r.json();
      setReports(Array.isArray(d) ? d : []);
    } catch (err) {
      toast("Failed to load reports", "error");
    }
  };

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const loadUsers = async () => {
    try {
      const r = await authFetch("/api/v1/member/userlistactive");
      if (!r.ok) return;
      const data = await r.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      toast("Failed to load users", "error");
    }
  };
  useEffect(() => {
    loadUsers();
  }, []);

  const handleReset = () => {
    setFromDate(today);
    setToDate(today);
    setStatusFilter("");
    setSearchText("");
    setSelectedUser("");
    loadReports(); // fresh data reload
  };

  useEffect(() => {
    loadReports();
  }, [fromDate, toDate, statusFilter, selectedUser]);

  useEffect(() => {
      const filtered = reports.filter((report) =>
        Object.values(report)
          .join(" ")
          .toLowerCase()
          .includes(searchText.toLowerCase())
      );
      setFilteredReports(filtered);
    }, [searchText, reports]);

  /* ---------- Table Columns ---------- */
  const columns = [
    {
      id: "date",
      name: "# / Date",
      cell: (row, i) => (
        <div>
          <div className="fw-bold">{i + 1}</div>
          <div className="text-danger fw-bold">{row.date}</div>
        </div>
      ),
      sortable: true,
      sortFunction: (a, b) =>
        dayjs(a.date, "DD MMM YY - hh:mm A").unix() -
        dayjs(b.date, "DD MMM YY - hh:mm A").unix(),
    },
    { name: "User Details", selector: (row) => row.user_details, wrap: false },
   
    { name: "Transaction ID", selector: (row) => row.transaction_id || "-" },
    { name: "Reference Details", selector: (row) => row.reference_details, wrap: true },
    { name: "Amount", selector: (row) => row.amount, sortable: true },
    { name: "Charge & GST", selector: (row) => row.charge, wrap: true },
    {
      name: "Status",
      cell: (row) => (
        <span
          className={`btn btn-sm btn-block radius-30 ${
            (row.status || "").toLowerCase() === "success"
              ? "btn-light-success text-black"
              : (row.status || "").toLowerCase() === "failed"
              ? "btn-light-danger"
              : "btn-light-warning"
          }`}
        >
          {row.status}
        </span>
      ),
      // { name: "Action"},
      sortable: true,
    },
  ];

  return (
    <>
      <Helmet>
        <title>Payin Report | SuvidhaPe</title>
      </Helmet>

      <div className="page-wrapper">
        <div className="page-content-wrapper">
          <div className="page-content">
            <div className="card">
              <div className="card-body">
                <h4 className="mb-3">Payin Reports</h4>

                {/* Filters + Export */}
                <div className="row mb-3 d-flex g-2 flex-wrap">
                  <div className="col-md-2 col-12">
                    <input type="date" className="form-control" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                  </div>
                  <div className="col-md-2 col-12">
                    <input type="date" className="form-control" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                  </div>
                  {/* <input type="text" placeholder="Search User" className="form-control w-auto" value={searchText} onChange={(e) => setSearchText(e.target.value)} /> */}
                  <div className="col-md-2 col-12">
                    <select className="form-select form-control" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                      <option value="">All Status</option>
                      <option value="pending">Pending</option>
                      <option value="success">Success</option>
                      <option value="failed">Failed</option>
                    </select>
                  </div>
                  <div className="col-md-2 col-12">
                    <select
                      className="form-select form-control"
                      value={selectedUser}
                      onChange={(e) => setSelectedUser(e.target.value)}
                    >
                      <option value="">All Users</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name} ({u.merchantcode})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-2 col-12">   
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Type to search..."
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                  </div>
                  <div className="col-md-2 col-12 w-auto">   
                    <button className="btn btn-primary btn-sm" onClick={loadReports}>Apply Filters</button>
                  </div>
                  <div className="col-md-2 col-6 w-auto">     
                    <button className="btn btn-success btn-sm" onClick={() => exportToExcel(reports)}><i class="bx bx-cloud-download me-1"></i>Download Excel</button>
                  </div>
                  <div className="col-md-2 col-6 w-auto">
                    <button className="btn btn-danger btn-sm" onClick={() => exportToPDF(reports)}><i class="bx bx-cloud-download me-1"></i>Download PDF</button>
                  </div>
                  <div className="col-md-2 col-6 w-auto">
                    <button className="btn btn-dark btn-sm" onClick={handleReset}><i className="bx bx-refresh me-1"></i>Reset</button>
                  </div>
                  <div className="col-md-6 col-12 pt-2 w-auto">
                    <div className="pt-2 badge bg-light text-dark text-end fw-bold d-block d-sm-flex">
                        <span className="me-3 d-flex">Total Amount: ₹{totalAmount.toFixed(2)}</span>
                        <span className="me-3 d-flex">Total Charge: ₹{Math.abs(totalCharge).toFixed(2)}</span>
                        <span className="me-3 d-flex">Total GST: ₹{Math.abs(totalGST).toFixed(2)}</span>
                        <span className="me-3 d-flex text-success">
                          Success Ratio: {successRatio}%
                        </span>

                        {/* <span className="me-3 d-flex">
                          Total Txn: {totalTxn}
                        </span>

                        <span className="me-3 d-flex">
                          Success Txn: {successTxn}
                        </span>

                        <span className="me-3 d-flex text-success">
                          Success Ratio: {successRatio}%
                        </span>

                        <span className="me-3 d-flex">
                          Total Amount: ₹{totalAmount.toFixed(2)}
                        </span>

                        <span className="me-3 d-flex">
                          Total Charge: ₹{Math.abs(totalCharge).toFixed(2)}
                        </span>

                        <span className="me-3 d-flex">
                          Total GST: ₹{Math.abs(totalGST).toFixed(2)}
                        </span> */}
                    </div>
                  </div> 
                </div>

                <DataTable 
                columns={columns} 
                // data={reports}
                data={filteredReports} 
                pagination 
                highlightOnHover 
                striped 
                defaultSortFieldId="date" 
                defaultSortAsc={false} 
                persistTableHead 
                paginationRowsPerPageOptions={[10, 25, 50, 100, 250, 500]} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
